import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Loader2, Volume2 } from 'lucide-react';
import useOrgData from '../../../application/hooks/useOrgData';
import { useToast } from '../../components/ui/toast';
import { Button } from '../../components/ui/button';
import { FormField } from '../../components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { cn } from '../../lib/utils';

const VOICE_OPTIONS = [
  { value: 'Matthew', label: 'Matthew', gender: 'Male', isDefault: true },
  { value: 'Danielle', label: 'Danielle', gender: 'Female', isDefault: false },
  { value: 'Joanna', label: 'Joanna', gender: 'Female', isDefault: false },
  { value: 'Ruth', label: 'Ruth', gender: 'Female', isDefault: false },
  { value: 'Salli', label: 'Salli', gender: 'Female', isDefault: false },
  { value: 'Stephen', label: 'Stephen', gender: 'Male', isDefault: false },
  { value: 'Tiffany', label: 'Tiffany', gender: 'Female', isDefault: false },
] as const;

const RATE_MIN = 0.5;
const RATE_MAX = 2.0;
const RATE_STEP = 0.1;
const RATE_DEFAULT = 1.0;

function rateToPercent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function percentToRate(percent: string): number {
  const num = parseInt(percent.replace('%', ''), 10);
  if (isNaN(num)) {
    return RATE_DEFAULT;
  }
  return Math.round(num / 10) / 10;
}

export function HankVoiceConfig() {
  const { orgData, updateOrgData } = useOrgData();
  const toast = useToast();

  const [pollyVoice, setPollyVoice] = useState<string>('Matthew');
  const [pollyRate, setPollyRate] = useState<number>(RATE_DEFAULT);
  const [originalVoice, setOriginalVoice] = useState<string>('Matthew');
  const [originalRate, setOriginalRate] = useState<number>(RATE_DEFAULT);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (orgData?.configParms) {
      const voice = orgData.configParms.pollyVoice || 'Matthew';
      const rateStr = orgData.configParms.pollyRate || '100%';
      const rate = percentToRate(rateStr);

      setPollyVoice(voice);
      setPollyRate(rate);
      setOriginalVoice(voice);
      setOriginalRate(rate);
      setIsLoaded(true);
    }
  }, [orgData]);

  const hasChanged =
    pollyVoice !== originalVoice || Math.abs(pollyRate - originalRate) > 0.001;

  const handleDiscard = useCallback(() => {
    setPollyVoice(originalVoice);
    setPollyRate(originalRate);
  }, [originalVoice, originalRate]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const newConfigParms = {
        ...orgData?.configParms,
        pollyVoice,
        pollyRate: rateToPercent(pollyRate),
      };

      const newOrgInput = {
        id: orgData?.id,
        configParms: JSON.stringify(newConfigParms),
      };

      const success = await updateOrgData(newOrgInput);
      if (success) {
        setOriginalVoice(pollyVoice);
        setOriginalRate(pollyRate);
        toast.success('Hank voice settings saved successfully');
      } else {
        toast.error('Failed to save voice settings');
      }
    } catch (err) {
      console.error('Error saving voice settings:', err);
      toast.error('Error saving voice settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const rateMarks = [0.5, 1.0, 1.5, 2.0];

  return (
    <div className="space-y-8 py-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10">
          <Volume2 className="size-5 text-primary" />
        </div>
        <div>
          <h3 className="text-base font-bold text-foreground">
            AI Voice Settings
          </h3>
          <p className="text-sm text-muted-foreground">
            Configure the voice and speed for Hank&apos;s AI responses.
          </p>
        </div>
      </div>

      {/* Voice Selection */}
      <div className="max-w-md space-y-6">
        <FormField label="Voice">
          <Select value={pollyVoice} onValueChange={setPollyVoice}>
            <SelectTrigger>
              <SelectValue placeholder="Select a voice" />
            </SelectTrigger>
            <SelectContent>
              {VOICE_OPTIONS.map((voice) => (
                <SelectItem key={voice.value} value={voice.value}>
                  {voice.label}, {voice.gender}
                  {voice.isDefault ? ' (default)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        {/* Speed Slider */}
        <FormField label="Speed">
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <input
                  type="range"
                  min={RATE_MIN}
                  max={RATE_MAX}
                  step={RATE_STEP}
                  value={pollyRate}
                  onChange={(e) =>
                    setPollyRate(
                      Math.round(parseFloat(e.target.value) * 10) / 10
                    )
                  }
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-muted accent-primary"
                />
                <div className="flex justify-between mt-2">
                  {rateMarks.map((mark) => (
                    <button
                      key={mark}
                      type="button"
                      onClick={() => setPollyRate(mark)}
                      className={cn(
                        'text-xs transition-colors',
                        Math.abs(pollyRate - mark) < 0.05
                          ? 'text-primary font-medium'
                          : 'text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {mark.toFixed(1)}x
                    </button>
                  ))}
                </div>
              </div>
              <span className="text-sm font-medium text-foreground min-w-[3rem] text-right">
                {pollyRate.toFixed(1)}x
              </span>
            </div>
          </div>
        </FormField>
      </div>

      {/* Footer buttons */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          onClick={handleDiscard}
          disabled={!hasChanged || isSaving}
          className="px-6 rounded-full"
        >
          Discard
        </Button>
        <Button
          onClick={handleSave}
          disabled={!hasChanged || isSaving}
          className="px-6 rounded-full"
        >
          {isSaving ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </div>
  );
}
